import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { MySqlLinkedService } from 'app/model/linkedservice/mysql.linkedservice';
import { akvToModel, akvToResource, parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { Constants } from 'app/shared/shared-constants';
import { isString } from 'app/shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { convertToSSLMode, convertToUseSystemTrustStore, mysqlAdditionalFieldsAllowedKeys, MysqlAdditionalPropertiesKeyType, SSLMode } from 'app/registries/consts/mysql-consts';
import { connectionStringPartToResource, connectionStringPartToModel } from 'app/shared/utils/linkedservice-parameter.util';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext, ITridentResource, ParameterConfig } from 'shell-models-lib';
import { ValueAdapter } from '../value.adapter';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { toParameterClass, toParameterObject } from './parameter-config.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(MySqlLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                server: '*',
                database: '*',
                username: '*',
                port: '*',
                password: 'secret',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: MySqlLinkedService) => {
                    // if password is in AKV, then connectionString won't contain secret
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV && ls.password instanceof AzureKeyVault) {
                        return '*';
                    }
                    return 'secret';
                },
                encryptedCredential: '*',
                sslMode: '*',
                useSystemTrustStore: '*',
                driverVersion: '*',
                // additional fields
                allowZeroDatetime: '*',
                connectionTimeout: '*',
                convertZeroDatetime: '*',
                guidFormat: '*',
                sslCert: '*',
                sslKey: '*',
                treatTinyAsBoolean: '*'
            },
            parameters: '*'
        }
    }
})
export class MySqlLinkedServiceAdapter extends LinkedServiceAdapter<MySqlLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => MySqlLinkedService {
        return MySqlLinkedService;
    }

    protected model(): IModel {
        return MySqlLinkedService.MODEL;
    }

    protected override toAzureTypeProperties = (tridentResource: ITridentResource): Partial<Model.MySqlLinkedServiceTypeProperties> => {
        try {
            const connectionDetails = JSON.parse(tridentResource.connectionDetails);
            return {
                server: connectionDetails.server,
                database: connectionDetails.database,
                driverVersion: driverVersions.v2
            };
        } catch (error) {
            return {};
        }
    };

    protected async propertiesToResourceAsync(linkedService: MySqlLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.MySqlLinkedService;
        properties.type = Metamodel.modelFor(MySqlLinkedService).name;

        if (linkedService.driverVersion === driverVersions.v2) {
            properties.typeProperties = {
                server: this.valueToResource(linkedService, MySqlLinkedService.SERVER_NAME),
                port: this.valueToResource(linkedService, MySqlLinkedService.PORT),
                database: this.valueToResource(linkedService, MySqlLinkedService.DATABASE_NAME),
                username: this.valueToResource(linkedService, MySqlLinkedService.USER_NAME),
                sslMode: linkedService.sslMode != null ? linkedService.sslMode : 1,
                useSystemTrustStore: linkedService.sslMode !== SSLMode.Disabled ? linkedService.useSystemTrustStore ? 1 : 0 : undefined,
                password: parameterizablePasswordOrAKVtoSecretBase(linkedService, MySqlLinkedService.PASSWORD, this.context),
                driverVersion: driverVersions.v2
            };

            if (linkedService.parameterizableAdditionalFields?.length > 0) {
                properties.typeProperties = {
                    ...properties.typeProperties,
                    ...toParameterObject(linkedService.parameterizableAdditionalFields, false, this.valueToResource)
                };
            }

        } else {
            properties.typeProperties = {
                connectionString: this._getConnectionStringResource(linkedService),
                password: !linkedService.enableAKV && linkedService.password instanceof AzureKeyVault ? akvToResource(linkedService.password, this.context) : undefined,
            };
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: MySqlLinkedService): Promise<MySqlLinkedService> {
        const properties = linkedServiceResource.properties as Model.MySqlLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.MySqlLinkedServiceTypeProperties = properties.typeProperties;

        // Cleanup, in case none of the if-else statement got executed
        MySqlLinkedService.SERVER_NAME.setValue(linkedService, undefined);
        MySqlLinkedService.SERVER_NAME.setTemplateValue(linkedService, undefined);
        MySqlLinkedService.DATABASE_NAME.setValue(linkedService, undefined);
        MySqlLinkedService.DATABASE_NAME.setTemplateValue(linkedService, undefined);
        MySqlLinkedService.PORT.setValue(linkedService, undefined);
        MySqlLinkedService.PORT.setTemplateValue(linkedService, undefined);
        MySqlLinkedService.USER_NAME.setValue(linkedService, undefined);
        MySqlLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
        MySqlLinkedService.PASSWORD.setValue(linkedService, undefined);
        MySqlLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);

        if (typeProperties.driverVersion === driverVersions.v2) {
            linkedService.driverVersion = driverVersions.v2;
            linkedService.isLegacyConnector = false;
            ValueAdapter.toModel(linkedService, MySqlLinkedService.SERVER_NAME, typeProperties.server);
            ValueAdapter.toModel(linkedService, MySqlLinkedService.DATABASE_NAME, typeProperties.database);
            ValueAdapter.toModel(linkedService, MySqlLinkedService.PORT, typeProperties.port);
            ValueAdapter.toModel(linkedService, MySqlLinkedService.USER_NAME, typeProperties.username);
            linkedService.sslMode = typeProperties.sslMode;
            linkedService.useSystemTrustStore = linkedService.sslMode !== SSLMode.Disabled ?
                convertToUseSystemTrustStore(`${typeProperties.useSystemTrustStore}`) : false;
            parameterizableSecretBaseToModel(linkedService, MySqlLinkedService.PASSWORD, properties.typeProperties.password, this.context);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const additionalPropertiesObj: { [key: string]: any } = {};
            for (const propertyName in typeProperties) {
                if (mysqlAdditionalFieldsAllowedKeys.includes(propertyName as MysqlAdditionalPropertiesKeyType)) {
                    additionalPropertiesObj[propertyName] =
                        typeProperties[propertyName as keyof Model.MySqlLinkedServiceTypeProperties];
                }
            }
            linkedService.parameterizableAdditionalFields = toParameterClass(
                additionalPropertiesObj,
                linkedService
            ) as Array<ParameterConfig<MysqlAdditionalPropertiesKeyType>>;

        } else {
            linkedService.driverVersion = driverVersions.v1;
            linkedService.isLegacyConnector = true;
            if (typeProperties.connectionString) {
                if (isString(typeProperties.connectionString)) {
                    const builder = new DbConnectionStringBuilderService(true);
                    builder.parse(typeProperties.connectionString as string);

                    connectionStringPartToModel(linkedService, MySqlLinkedService.SERVER_NAME, builder.getValue('Server', false) || '');
                    connectionStringPartToModel(linkedService, MySqlLinkedService.DATABASE_NAME, builder.getValue('Database', false) || '');
                    connectionStringPartToModel(linkedService, MySqlLinkedService.PORT, builder.getValue('Port', false));
                    connectionStringPartToModel(linkedService, MySqlLinkedService.USER_NAME, builder.getValue('User', false) || builder.getValue('UID', false) || '');
                    linkedService.sslMode = convertToSSLMode(builder.getValue('SSLMode', false));
                    linkedService.useSystemTrustStore = linkedService.sslMode !== SSLMode.Disabled ?
                        convertToUseSystemTrustStore(builder.getValue('UseSystemTrustStore', false)) : false;
                    connectionStringPartToModel(linkedService, MySqlLinkedService.PASSWORD, builder.getValue('Password', false) || '', true);
                    linkedService.additionalFields = builder.getRemainedKeyValuePairs(['Server', 'Port', 'Database', 'User', 'UID', 'Password', 'SSLMode', 'UseSystemTrustStore']);
                    if (properties.typeProperties.password) {
                        parameterizableSecretBaseToModel(linkedService, MySqlLinkedService.PASSWORD, properties.typeProperties.password, this.context);
                    }
                } else if (SecureStringReplacer.isSecureStringObject(typeProperties.connectionString)) {
                    linkedService.server = Constants.maskedCredential;
                    linkedService.database = Constants.maskedCredential;
                    linkedService.username = Constants.maskedCredential;
                    linkedService.password = Constants.maskedCredential;
                    if (properties.typeProperties.password) {
                        parameterizableSecretBaseToModel(linkedService, MySqlLinkedService.PASSWORD, properties.typeProperties.password, this.context);
                    }
                } else if (typeProperties.connectionString && (typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                    linkedService.referedAKV = akvToModel(typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
                    linkedService.enableAKV = true;
                }
            } else {
                // handle legacy property bag
                /* eslint-disable @typescript-eslint/no-explicit-any */
                linkedService.server = (typeProperties as any).server;
                linkedService.database = (typeProperties as any).database;
                linkedService.username = (typeProperties as any).username;
                linkedService.password = Constants.maskedCredential;
                /* eslint-enable @typescript-eslint/no-explicit-any */
            }
        }
        return linkedService;
    }

    private _getConnectionStringResource(linkedService: MySqlLinkedService): string | Model.SecretBase {
        let connectionString: string | Model.SecretBase;
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('Server', connectionStringPartToResource(linkedService, MySqlLinkedService.SERVER_NAME));
            builder.add('Port', connectionStringPartToResource(linkedService, MySqlLinkedService.PORT));
            builder.add('Database', connectionStringPartToResource(linkedService, MySqlLinkedService.DATABASE_NAME));
            builder.add('User', connectionStringPartToResource(linkedService, MySqlLinkedService.USER_NAME));
            builder.add('SSLMode', linkedService.sslMode != null ? linkedService.sslMode.toString() : '1');
            if (linkedService.sslMode !== SSLMode.Disabled) {
                builder.add('UseSystemTrustStore', linkedService.useSystemTrustStore ? '1' : '0');
            }
            if (!(linkedService.password instanceof AzureKeyVault)) {
                const password = connectionStringPartToResource(linkedService, MySqlLinkedService.PASSWORD);
                if (password) {
                    builder.add('Password', password);
                }
            }

            if (linkedService.additionalFields) {
                linkedService.additionalFields.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });
            }
            connectionString = linkedService.server === Constants.maskedCredential &&
                linkedService.database === Constants.maskedCredential &&
                linkedService.username === Constants.maskedCredential &&
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                linkedService.password === Constants.maskedCredential ? {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }
        return connectionString;
    }
}
